import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, RefreshCw, Bell, BellOff, ArrowRight, Plus, Plane } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../../contexts/AuthContext";
import { FlightStatusBadge } from "./FlightStatusBadge";
import { BoardingCountdown } from "./BoardingCountdown";
import { AddFlightForm } from "./AddFlightForm";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA  = { fontFamily: "'Lora', Georgia, serif" };

function formatTime(isoStr) {
  if (!isoStr) return "";
  const normalized = isoStr.replace("T", " ");
  const timePart = normalized.split(" ")[1] || "";
  return timePart.slice(0, 5);
}

function TodayFlightView({ flight, tripId, onRefreshed }) {
  const [refreshing, setRefreshing] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(flight.notifications_enabled);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/flights/${flight.id}/refresh`);
      onRefreshed?.(data);
      toast.success("Mis à jour");
    } catch { toast.error("Actualisation impossible"); }
    finally { setRefreshing(false); }
  };

  const handleToggleNotif = async () => {
    const newVal = !notifEnabled;
    if (newVal && "Notification" in window) {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications refusées"); return; }
    }
    try {
      await axios.patch(`${API}/trips/${tripId}/flights/${flight.id}/notifications`, { enabled: newVal });
      setNotifEnabled(newVal);
      toast.success(newVal ? "Alertes activées" : "Alertes désactivées");
    } catch { toast.error("Erreur"); }
  };

  const depSched   = formatTime(flight.dep_scheduled);
  const depRevised = flight.dep_revised ? formatTime(flight.dep_revised) : null;
  const isDelayed  = depRevised && depRevised !== depSched;

  const cacheLabel = flight.from_cache
    ? `Hors ligne · ${flight.last_fetched_at ? new Date(flight.last_fetched_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "–"}`
    : "";

  return (
    <div className="space-y-4" data-testid="flight-sheet-content">
      {/* Flight number + status */}
      <div className="text-center">
        <p className="text-stone-400 text-xs uppercase tracking-widest mb-1" style={LORA}>
          {flight.segment_label}
        </p>
        <h2 className="text-5xl text-white mb-2" style={{ ...SERIF, fontWeight: 700 }}>
          {flight.flight_number}
        </h2>
        <FlightStatusBadge status={flight.status} size="lg" />
      </div>

      {/* Gate — big, most important info at airport */}
      {flight.dep_gate && (
        <div className="text-center bg-[#C17F59]/15 border border-[#C17F59]/30 rounded-2xl py-4">
          <p className="text-stone-400 text-xs uppercase tracking-widest mb-1" style={LORA}>Porte</p>
          <p className="text-6xl font-bold text-[#C17F59]" style={SERIF}>{flight.dep_gate}</p>
          {flight.dep_terminal && (
            <p className="text-stone-400 text-sm mt-1" style={LORA}>Terminal {flight.dep_terminal}</p>
          )}
        </div>
      )}

      {/* Route + times */}
      <div className="rounded-2xl p-4" style={{ background: "#3A3330" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-center">
            <p className="text-2xl font-bold text-white" style={SERIF}>{flight.departure_airport_code || "—"}</p>
            <p className="text-stone-400 text-xs" style={LORA}>{flight.departure_airport_name}</p>
          </div>
          <ArrowRight size={18} strokeWidth={1.5} className="text-[#C17F59]" />
          <div className="text-center">
            <p className="text-2xl font-bold text-white" style={SERIF}>{flight.arrival_airport_code || "—"}</p>
            <p className="text-stone-400 text-xs" style={LORA}>{flight.arrival_airport_name}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 border-t border-stone-700/50 pt-3">
          <div>
            <p className="text-stone-500 text-xs uppercase tracking-widest" style={LORA}>Départ prévu</p>
            <div className="flex items-baseline gap-1.5">
              <p className="text-white text-xl" style={SERIF}>{depSched || "—"}</p>
              {isDelayed && <p className="text-amber-400 text-lg" style={SERIF}>{depRevised}</p>}
            </div>
          </div>
          <div>
            <p className="text-stone-500 text-xs uppercase tracking-widest" style={LORA}>Arrivée prévue</p>
            <p className="text-white text-xl" style={SERIF}>
              {formatTime(flight.arr_revised || flight.arr_scheduled) || "—"}
            </p>
          </div>
        </div>
      </div>

      {/* Boarding countdown */}
      {flight.boarding_countdown && <BoardingCountdown countdown={flight.boarding_countdown} />}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#C17F59] text-white text-sm disabled:opacity-50 transition-opacity"
          style={LORA}
          data-testid="sheet-refresh-btn"
        >
          <RefreshCw size={14} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Actualisation…" : "Actualiser"}
        </button>
        <button
          onClick={handleToggleNotif}
          className={`px-4 py-3 rounded-2xl border text-sm transition-colors ${
            notifEnabled
              ? "bg-stone-700 border-stone-600 text-white"
              : "border-stone-600 text-stone-400 hover:border-[#C17F59]"
          }`}
          style={LORA}
          data-testid="sheet-notif-toggle"
        >
          {notifEnabled ? <Bell size={16} strokeWidth={1.5} /> : <BellOff size={16} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Cache label */}
      {cacheLabel && (
        <p className="text-stone-500 text-xs text-center italic" style={LORA}>{cacheLabel}</p>
      )}
    </div>
  );
}

// ── Main FlightSheet bottom sheet ──────────────────────────────────────────────
export function FlightSheet({ tripId, defaultDate, open, onClose }) {
  const [flights,  setFlights]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  const loadFlights = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/flights`);
      setFlights(data);
    } catch { toast.error("Impossible de charger vos vols. Vérifiez votre connexion.", { id: "flights-load-error" }); }
    finally { setLoading(false); }
  }, [tripId, open]);

  useEffect(() => { loadFlights(); }, [loadFlights]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const today = new Date().toISOString().slice(0, 10);
  const todayFlights = flights.filter((f) => f.flight_date === today);

  const handleAdded = (f) => setFlights((prev) => [...prev, f]);
  const handleRefreshed = (updated) =>
    setFlights((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl overflow-y-auto"
        style={{
          background: "#1C1917",
          maxWidth: 480,
          margin: "0 auto",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxHeight: "90vh",
        }}
        data-testid="flight-sheet"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-stone-700" />
        </div>

        <div className="px-5 pb-6 pt-2">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Plane size={16} strokeWidth={1.5} className="text-[#C17F59]" />
              <h2 className="text-white text-xl" style={{ ...SERIF, fontWeight: 600 }}>Mon Vol en Direct</h2>
            </div>
            <button onClick={onClose} className="text-stone-400 hover:text-white p-1">
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-stone-400 text-sm italic" style={LORA}>Chargement…</p>
            </div>
          ) : todayFlights.length > 0 ? (
            <TodayFlightView
              flight={todayFlights[0]}
              tripId={tripId}
              onRefreshed={handleRefreshed}
            />
          ) : (
            <div className="space-y-4">
              <p className="text-stone-400 text-sm italic text-center" style={LORA}>
                Aucun vol enregistré pour aujourd'hui.
              </p>
              {/* Inline add form, styled for dark bg */}
              <div>
                <div className="border border-stone-700 rounded-2xl p-4" style={{ background: "#2B2623" }}>
                  <p className="text-stone-300 text-xs uppercase tracking-widest mb-3" style={LORA}>
                    Ajouter un vol pour aujourd'hui
                  </p>
                  <AddFlightForm
                    tripId={tripId}
                    defaultDate={defaultDate || today}
                    onAdded={handleAdded}
                    disabled={flights.length >= 4}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div style={{ height: "env(safe-area-inset-bottom, 16px)" }} />
      </div>
    </>
  );
}
